// M0 spike art: plain Canvas2D, no Phaser. Replaced by art/ + BootScene bake in M5/M8.
// Throwaway colours (the canonical palette arrives with palette.json in M1).

export const SPIKE_TILE = 128;
export const SPIKE_BOARD_KEY = 'spike-board';
export const SPIKE_PROBE_KEY = 'spike-probe';

const WOOD_LIGHT = '#d8c29a';
const WOOD_DARK = '#b89e70';
const EDGE = '#8a7250';
const FRAME = '#5a4632';
const HIGHLIGHT = '#fe6a00';

function makeCanvas(w: number, h: number): { c: HTMLCanvasElement; g: CanvasRenderingContext2D } {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d');
  if (!g) throw new Error('Canvas2D unavailable');
  return { c, g };
}

/** 512×512 (power of two) sheet: frames light, dark, hl, white, frame — each 128×128 in row 0 / row 1. */
export function drawBoardSheet(): {
  canvas: HTMLCanvasElement;
  frames: Record<string, [number, number]>;
} {
  const T = SPIKE_TILE;
  const { c, g } = makeCanvas(4 * T, 4 * T);
  const tile = (ox: number, fill: string): void => {
    g.fillStyle = fill;
    g.fillRect(ox, 0, T, T);
    g.strokeStyle = EDGE;
    g.lineWidth = 4;
    g.strokeRect(ox + 2, 2, T - 4, T - 4);
  };
  tile(0, WOOD_LIGHT);
  tile(T, WOOD_DARK);
  // highlight ring: transparent centre
  g.strokeStyle = HIGHLIGHT;
  g.lineWidth = 12;
  g.strokeRect(2 * T + 8, 8, T - 16, T - 16);
  g.fillStyle = 'rgba(254,106,0,0.18)';
  g.fillRect(2 * T + 14, 14, T - 28, T - 28);
  g.fillStyle = '#ffffff';
  g.fillRect(3 * T, 0, T, T);
  g.fillStyle = FRAME;
  g.fillRect(0, T, T, T);
  return {
    canvas: c,
    frames: { light: [0, 0], dark: [T, 0], hl: [2 * T, 0], white: [3 * T, 0], frame: [0, T] },
  };
}

/**
 * 256×256 (power of two) probe sheet for `?spike=1`: q0 white, q1 red, q2 green, q3 a 1-px checkerboard.
 * q3 shown at 1/31 scale samples texel centres: without mipmaps a pixel reads pure black/white, with
 * LINEAR_MIPMAP_LINEAR it reads mid grey.
 */
export function drawProbeSheet(): HTMLCanvasElement {
  const Q = 128;
  const { c, g } = makeCanvas(2 * Q, 2 * Q);
  g.fillStyle = '#ffffff';
  g.fillRect(0, 0, Q, Q);
  g.fillStyle = '#ff0000';
  g.fillRect(Q, 0, Q, Q);
  g.fillStyle = '#00ff00';
  g.fillRect(0, Q, Q, Q);
  const img = g.createImageData(Q, Q);
  for (let y = 0; y < Q; y++) {
    for (let x = 0; x < Q; x++) {
      const v = (x + y) % 2 === 0 ? 255 : 0;
      const i = (y * Q + x) * 4;
      img.data[i] = v;
      img.data[i + 1] = v;
      img.data[i + 2] = v;
      img.data[i + 3] = 255;
    }
  }
  g.putImageData(img, Q, Q);
  return c;
}
